/**
 * Pure helpers for the availability table, the care-manager map and the
 * ranked "place a client" lists.
 *
 * Week arithmetic mirrors api/_shared/capacity/week.ts (Monday..Sunday,
 * decided in the tenant's operating zone). The new-client stance is the
 * one the server sent on each capacity row (new_client_stance), never
 * derived here and never computed from hours: a target is an average
 * over time, not a weekly cap. A row the server sent without it shows
 * no stance.
 */

export const CAPACITY_TIME_ZONE = 'America/New_York';

/** The longest note a week's availability takes, as the server's
 *  validator refuses past it (NOTES_MAX, api/_shared/validators/
 *  staffAvailability.ts; a test pins the two together). */
export const AVAILABILITY_NOTES_MAX = 4000;

/**
 * The office's stance on a person taking a new client, per week, as
 * stored: Open; Talk to Me, the office talks to them before assigning one
 * (ranked with Open); or Not Open. Set on Team, Availability.
 */
export type NewClientStance = 'open' | 'talk_to_me' | 'not_open';

/**
 * The new-client stance the server decided for the week from what the
 * office recorded: the stored stance, or On leave, which outranks it. The
 * server sends it on each capacity row as new_client_stance, and on each
 * assignment suggestion as stated_availability, only to a seat that sees
 * it; the browser reads it as sent and derives nothing. Nothing here is
 * computed from hours, so no surface subtracts worked hours from a target
 * or calls anyone near capacity or full; that reading belongs to the
 * person assigning.
 */
export type StatedAvailability = NewClientStance | 'on_leave';

/** The segmented toggle's order on the board. */
export const STANCE_OPTIONS: readonly NewClientStance[] = ['open', 'talk_to_me', 'not_open'];

export const STATED_AVAILABILITY_LABEL: Record<StatedAvailability, string> = {
  open: 'Open',
  talk_to_me: 'Talk to Me',
  not_open: 'Not Open',
  on_leave: 'On leave',
};

/** Each stance's fill and ink, as classes: the stance chips and the
 *  drawer pill. Each fill is the switch's look for the stance (Open the
 *  primary forest, Talk to Me the gold, Not Open the faded terracotta),
 *  through a stance token that points at the look's token in
 *  src/index.css, so there is no second hex. The words read on each at
 *  AA or better. */
export const STANCE_CLASSES: Record<NewClientStance, string> = {
  open: 'bg-stance-open text-cream',
  talk_to_me: 'bg-stance-talk-to-me text-forest-700',
  not_open: 'bg-stance-not-open text-ink',
};

/** The same fills as values, for Leaflet's pin centres and the card dot,
 *  which take a colour, not a class: --color-sage-800, --color-warm-400
 *  and --color-terracotta-soft. A test pins them equal to the tokens. An
 *  Open pin keeps its unit's fill on the map. */
export const STANCE_FILL: Record<NewClientStance, string> = {
  open: '#1B4332',
  talk_to_me: '#C4A265',
  not_open: '#EDA893',
};

/** The drawer's pill classes: the stance fills, and On leave's own grey
 *  (--color-on-leave, the map's CM_ON_LEAVE_FILL), never a stance's
 *  colour. */
export const STATED_AVAILABILITY_CLASSES: Record<StatedAvailability, string> = {
  ...STANCE_CLASSES,
  on_leave: 'bg-on-leave text-ink',
};

/**
 * CmCard's status dot per state, beside the stance's words: the stance
 * fill, and a dark grey on leave. The dot sits on the card's cream and is
 * aria-hidden, so it is exempt from the map's base contrast floor; the
 * map pin's own centres are in CapacityMapPanel (STANCE_FILL, and
 * CM_ON_LEAVE_FILL on leave). Pinned in tests/ui/mapColors.test.ts.
 */
export const STATED_AVAILABILITY_DOT: Record<StatedAvailability, string> = {
  ...STANCE_FILL,
  on_leave: '#6B6B6B',
};

/** Whether a stance ranks as available: Open and Talk to Me do, together. */
export function isAvailableStance(stance: StatedAvailability): boolean {
  return stance === 'open' || stance === 'talk_to_me';
}

/**
 * The stance stored for the week, the one the board's toggle and chip
 * show: the override's own stance, whether or not the week is on leave;
 * with no override, the stance the server sent for the week. undefined
 * when the server sent neither.
 */
export function storedStance(row: {
  availability?: { new_client_stance: NewClientStance } | null;
  new_client_stance?: StatedAvailability;
}): NewClientStance | undefined {
  if (row.availability) return row.availability.new_client_stance;
  if (row.availability === null && row.new_client_stance !== undefined && row.new_client_stance !== 'on_leave') {
    return row.new_client_stance;
  }
  return undefined;
}

/**
 * The ranked lists' two blocks, from the stance the server sent: Open
 * and Talk to Me first, together, then Not Open and On leave, each block
 * keeping the order it arrived in (nearest first, from rankByDistance).
 * null when any row carries no stance: there are no blocks to draw, and
 * the caller shows one list with no stance.
 */
export function partitionByAvailability<T>(
  rows: readonly T[],
  stanceOf: (row: T) => StatedAvailability | undefined,
): { available: T[]; unavailable: T[] } | null {
  const available: T[] = [];
  const unavailable: T[] = [];
  for (const row of rows) {
    const stance = stanceOf(row);
    if (stance === undefined) return null;
    (isAvailableStance(stance) ? available : unavailable).push(row);
  }
  return { available, unavailable };
}

/** "20" for whole hours, "17.5" otherwise; a numeric-text column in, a label out. */
export function formatHours(value: string | number | null): string {
  if (value === null || value === undefined) return '';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '';
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, '');
}

export function calendarDateIn(instant: Date, timeZone: string = CAPACITY_TIME_ZONE): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const pick = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  return `${pick('year')}-${pick('month')}-${pick('day')}`;
}

export function addDays(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d) + days * 86_400_000).toISOString().slice(0, 10);
}

export function isoWeekMonday(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return addDays(isoDate, -(dow === 0 ? 6 : dow - 1));
}

export function currentWeekMonday(now: Date = new Date()): string {
  return isoWeekMonday(calendarDateIn(now));
}

/** "Aug 31 to Sep 6, 2026" for the week picker. */
export function weekLabel(monday: string): string {
  const sunday = addDays(monday, 6);
  const fmt = (iso: string, withYear: boolean): string => {
    const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
      ...(withYear ? { year: 'numeric' } : {}),
    });
  };
  return `${fmt(monday, false)} to ${fmt(sunday, true)}`;
}

// ---------------------------------------------------------------------
// The "place a client" panel's SPICE list: the one surface that reads a
// therapist's schedule in sessions (planned against the hours target at
// one hour per session, and which weekdays are open).
// ---------------------------------------------------------------------

/** "5 of 7 sessions planned" / "No target set": the SPICE list's cell
 *  copy in the "place a client" panel. The target is the row's hours
 *  target read as sessions, one hour each. */
export function sessionsPlannedLabel(planned: number, targetHours: string | null): string {
  const target = weeklyTargetHours({ target_weekly_hours: targetHours });
  if (target === null) return 'No target set';
  return `${planned} of ${formatHours(target)} session${target === 1 ? '' : 's'} planned`;
}

const SHORT_WEEKDAY_NAMES: Record<number, string> = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri' };

/** "Thursdays are open" — the schedules doc's own phrase — for one or
 *  more open weekdays; "" when none (no target, or every day is full). */
export function openWeekdaysLabel(weekdays: readonly number[]): string {
  if (weekdays.length === 0) return '';
  const names = weekdays.map((wd) => SHORT_WEEKDAY_NAMES[wd] ?? '').filter(Boolean);
  const plural = names.length === 1 ? 'is' : 'are';
  return `${names.join(', ')} ${plural} open`;
}

// ---------------------------------------------------------------------
// The merged availability table (Team > Availability): one table for
// every billable staff member, care managers and SPICE therapists
// together, grouped by unit.
// ---------------------------------------------------------------------

/**
 * The Target cell's hours for one row: target_weekly_hours as a number,
 * the one target every row kind carries (a SPICE session is an hour);
 * null when it is not set, which the cell renders as "Not set".
 */
export function weeklyTargetHours(row: { target_weekly_hours: string | null }): number | null {
  if (row.target_weekly_hours === null) return null;
  const hours = Number(row.target_weekly_hours);
  return Number.isFinite(hours) ? hours : null;
}

export interface UnitGroupable {
  unit_name: string | null;
}

export interface CapacityUnitGroup<T> {
  /** The header text: the unit's name, or "No unit yet". */
  label: string;
  unitName: string | null;
  rows: T[];
}

/**
 * Buckets rows by unit_name: one group per unit (alphabetical by unit
 * name), then a final "No unit yet" group for rows with none — the same
 * order the roster's own unit sort settles on (teamDirectory.ts
 * sortRoster/groupSortedRosterByUnit), so the two tables never disagree
 * about where a unit run sits. Each unit's rows keep the order they
 * arrived in; sort by name first if that's the order you want within a
 * unit (the roster sorts by name before grouping — same contract here).
 */
export function groupByUnit<T extends UnitGroupable>(rows: readonly T[]): CapacityUnitGroup<T>[] {
  const byUnit = new Map<string, T[]>();
  const withoutUnit: T[] = [];
  for (const row of rows) {
    if (row.unit_name === null) {
      withoutUnit.push(row);
      continue;
    }
    const list = byUnit.get(row.unit_name);
    if (list) list.push(row);
    else byUnit.set(row.unit_name, [row]);
  }
  const unitNames = [...byUnit.keys()].sort((a, b) => a.localeCompare(b));
  const groups: CapacityUnitGroup<T>[] = unitNames.map((name) => ({
    label: name,
    unitName: name,
    rows: byUnit.get(name)!,
  }));
  if (withoutUnit.length > 0) groups.push({ label: 'No unit yet', unitName: null, rows: withoutUnit });
  return groups;
}
